/**
 * Add by IP Modal
 * ===============
 * Modal for looking up device info by IP address using MCP tools.
 * Handles credential prompts for hosts without stored API tokens.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { X, Search, Server, Wifi, AlertCircle, CheckCircle, Key, Loader2 } from 'lucide-react'
import { assetApi, credentialsApi } from '@/services/api'
import Button from '@/components/Button'
import Input from '@/components/Input'
import type { IPLookupResponse, IPLookupAutoFill, InfrastructureSource, AddCredentialRequest } from '@/types'
import { ASSET_TYPE_LABELS, INFRASTRUCTURE_SOURCE_LABELS } from '@/types'
import { formatStorageCapacity } from '@/utils/storage'

interface AddByIPModalProps {
  isOpen: boolean
  onClose: () => void
  /** Optional: Restrict lookup to specific infrastructure source */
  source?: InfrastructureSource
  /** Optional: Path to return to after form submission (stored in navigation state) */
  returnPath?: string
}

export default function AddByIPModal({ isOpen, onClose, source, returnPath }: AddByIPModalProps) {
  const navigate = useNavigate()
  const [ipAddress, setIpAddress] = useState('')
  const [lookupResult, setLookupResult] = useState<IPLookupResponse | null>(null)

  // Credential prompt state
  const [showCredentialPrompt, setShowCredentialPrompt] = useState(false)
  const [credentialHost, setCredentialHost] = useState<string | null>(null)
  const [credentialPort, setCredentialPort] = useState<number>(8006)
  const [credentialServiceType, setCredentialServiceType] = useState<'proxmox' | 'pbs'>('proxmox')
  const [username, setUsername] = useState('root@pam')
  const [password, setPassword] = useState('')
  const [credentialError, setCredentialError] = useState<string | null>(null)

  const lookupMutation = useMutation({
    mutationFn: (ip: string) => assetApi.lookupByIP(ip, source),
    onSuccess: (response) => {
      if (response.data) {
        setLookupResult(response.data)

        // Check if credentials are required
        if (response.data.credentials_required && response.data.credentials_host) {
          setCredentialHost(response.data.credentials_host)
          setCredentialPort(response.data.credentials_port || 8006)
          setCredentialServiceType((response.data.credentials_service_type as 'proxmox' | 'pbs') || 'proxmox')
          setShowCredentialPrompt(true)
        }
      }
    },
  })

  const credentialMutation = useMutation({
    mutationFn: (data: AddCredentialRequest) => credentialsApi.addProxmoxCredentials(data),
    onSuccess: () => {
      // Credentials stored successfully, re-run the lookup
      setShowCredentialPrompt(false)
      setCredentialError(null)
      setPassword('')
      // Re-run the lookup with the same IP
      lookupMutation.mutate(ipAddress.trim())
    },
    onError: (error: Error) => {
      setCredentialError(error.message || 'Authentication failed. Please check your credentials.')
    },
  })

  const handleLookup = () => {
    if (!ipAddress.trim()) return
    setLookupResult(null)
    setShowCredentialPrompt(false)
    setCredentialError(null)
    lookupMutation.mutate(ipAddress.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !lookupMutation.isPending) {
      handleLookup()
    }
  }

  const handleCredentialKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !credentialMutation.isPending && password) {
      handleCredentialSubmit()
    }
  }

  const handleCredentialSubmit = () => {
    if (!credentialHost || !username || !password) return

    credentialMutation.mutate({
      host: credentialHost,
      port: credentialPort,
      username,
      password,
      service_type: credentialServiceType,
    })
  }

  const handleProceed = () => {
    if (!lookupResult?.auto_fill) return

    // Navigate to asset form with pre-filled data
    navigate('/assets/new', {
      state: {
        prefill: lookupResult.auto_fill,
        source: lookupResult.source,
        returnPath: returnPath,  // Store return path for post-save navigation
      },
    })
    onClose()
  }

  const handleReset = () => {
    setIpAddress('')
    setLookupResult(null)
    setShowCredentialPrompt(false)
    setCredentialHost(null)
    setCredentialPort(8006)
    setCredentialServiceType('proxmox')
    setUsername('root@pam')
    setPassword('')
    setCredentialError(null)
    lookupMutation.reset()
    credentialMutation.reset()
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  if (!isOpen) return null

  const getSourceIcon = (source: string | null) => {
    if (source === 'proxmox') return <Server className="text-blue-500" size={20} />
    if (source === 'unifi') return <Wifi className="text-green-500" size={20} />
    return null
  }

  const getSourceLabel = (source: string | null) => {
    if (source === 'proxmox') return 'Proxmox'
    if (source === 'unifi') return 'UniFi'
    return 'Unknown'
  }

  const getServiceTypeLabel = (serviceType: 'proxmox' | 'pbs') => {
    return serviceType === 'pbs' ? 'Proxmox Backup Server' : 'Proxmox VE'
  }

  const renderAutoFillPreview = (autoFill: IPLookupAutoFill) => {
    const fields = [
      { label: 'Name', value: autoFill.name },
      { label: 'Type', value: autoFill.asset_type ? ASSET_TYPE_LABELS[autoFill.asset_type] : null },
      { label: 'Model', value: autoFill.model },
      { label: 'IP Address', value: autoFill.ip_address },
      { label: 'MAC Address', value: autoFill.mac_address },
      { label: 'CPU', value: autoFill.cpu_model },
      { label: 'Cores/Threads', value: autoFill.cpu_cores ? `${autoFill.cpu_cores} / ${autoFill.cpu_threads || '-'}` : null },
      { label: 'RAM', value: autoFill.ram_gb ? `${autoFill.ram_gb} GB` : null },
      { label: 'Storage', value: formatStorageCapacity(autoFill.storage_capacity_gb) },
      { label: 'Network Ports', value: autoFill.network_ports?.toString() },
      { label: 'Network Speed', value: autoFill.network_speed },
      { label: 'Firmware', value: autoFill.firmware_version },
    ].filter(f => f.value)

    return (
      <div className="grid grid-cols-2 gap-2 text-sm">
        {fields.map((field, idx) => (
          <div key={idx} className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">{field.label}:</span>
            <span className="text-gray-900 dark:text-white font-medium ml-2 truncate max-w-[180px]">
              {field.value}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Asset by IP
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* IP Input */}
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {source
                ? `Enter an IP address to lookup device information from ${INFRASTRUCTURE_SOURCE_LABELS[source]}.`
                : 'Enter an IP address to lookup device information from Proxmox or UniFi.'}
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., 192.168.10.252"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={lookupMutation.isPending || credentialMutation.isPending}
                className="flex-1"
              />
              <Button
                onClick={handleLookup}
                loading={lookupMutation.isPending}
                disabled={!ipAddress.trim() || credentialMutation.isPending}
                icon={<Search size={16} />}
              >
                Lookup
              </Button>
            </div>
          </div>

          {/* Error State */}
          {lookupMutation.isError && !showCredentialPrompt && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-700 dark:text-red-300 font-medium">Lookup Failed</p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {(lookupMutation.error as Error)?.message || 'An error occurred while looking up the device.'}
                </p>
              </div>
            </div>
          )}

          {/* Credential Prompt State */}
          {showCredentialPrompt && credentialHost && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                <Key className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-orange-700 dark:text-orange-300 font-medium">
                    Credentials Required
                  </p>
                  <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                    No API token found for <strong>{credentialHost}</strong> ({getServiceTypeLabel(credentialServiceType)}).
                    Enter your credentials to create an API token.
                  </p>
                </div>
              </div>

              {/* Permission info box */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
                <AlertCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={16} />
                <div className="text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">After authentication:</p>
                  <p>
                    {credentialServiceType === 'pbs' ? (
                      <>Add <strong>Audit</strong> permission to the token in PBS → Configuration → Access Control → Permissions (Path: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">/</code>)</>
                    ) : (
                      <>Add <strong>PVEAuditor</strong> role to the token in PVE → Datacenter → Permissions (Path: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">/</code>)</>
                    )}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username
                  </label>
                  <Input
                    placeholder="root@pam"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={credentialMutation.isPending}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Include the realm (e.g., root@pam, admin@pve)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleCredentialKeyDown}
                    disabled={credentialMutation.isPending}
                  />
                </div>

                {credentialError && (
                  <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{credentialError}</span>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCredentialPrompt(false)
                      setPassword('')
                      setCredentialError(null)
                    }}
                    disabled={credentialMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCredentialSubmit}
                    disabled={!password || credentialMutation.isPending}
                  >
                    {credentialMutation.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin mr-2" />
                        Authenticating...
                      </>
                    ) : (
                      'Authenticate'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Not Found State */}
          {lookupResult && !lookupResult.found && !showCredentialPrompt && (
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-yellow-700 dark:text-yellow-300 font-medium">Device Not Found</p>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                  {lookupResult.message || `No device found at IP ${ipAddress}`}
                </p>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                  You can still add the asset manually.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    navigate('/assets/new', {
                      state: {
                        prefill: { ip_address: ipAddress },
                        returnPath: returnPath,
                      },
                    })
                    onClose()
                  }}
                >
                  Add Manually
                </Button>
              </div>
            </div>
          )}

          {/* Success State */}
          {lookupResult?.found && lookupResult.auto_fill && !showCredentialPrompt && (
            <div className="space-y-4">
              {/* Source Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getSourceIcon(lookupResult.source)}
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Found in {getSourceLabel(lookupResult.source)}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircle size={16} />
                  <span className="text-sm font-medium">
                    {Math.round(lookupResult.confidence * 100)}% confidence
                  </span>
                </div>
              </div>

              {/* Preview Card */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                  Device Information
                </h3>
                {renderAutoFillPreview(lookupResult.auto_fill)}
              </div>

              {/* Network Interfaces Preview */}
              {lookupResult.auto_fill.network_interfaces && lookupResult.auto_fill.network_interfaces.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Network Interfaces ({lookupResult.auto_fill.network_interfaces.length})
                  </h4>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    Multiple network interfaces detected - they will be added to the form.
                  </div>
                </div>
              )}

              {/* Storage Drives Preview */}
              {lookupResult.auto_fill.storage_drives && lookupResult.auto_fill.storage_drives.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                  <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">
                    Storage Drives ({lookupResult.auto_fill.storage_drives.length})
                  </h4>
                  <div className="text-sm text-purple-700 dark:text-purple-300">
                    Multiple storage drives detected - they will be added to the form.
                  </div>
                </div>
              )}

              {/* Storage Volumes Preview */}
              {lookupResult.auto_fill.storage_volumes && lookupResult.auto_fill.storage_volumes.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                  <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">
                    Storage Volumes ({lookupResult.auto_fill.storage_volumes.length})
                  </h4>
                  <div className="text-sm text-orange-700 dark:text-orange-300">
                    Volume usage data detected - will be displayed on the asset page.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          {lookupResult?.found && !showCredentialPrompt ? (
            <Button onClick={handleProceed}>
              Continue to Form
            </Button>
          ) : lookupResult && !lookupResult.found && !showCredentialPrompt ? null : !showCredentialPrompt ? (
            <Button
              variant="secondary"
              onClick={() => {
                navigate('/assets/new', {
                  state: { returnPath: returnPath },
                })
                onClose()
              }}
            >
              Add Manually
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
